
import React from "react";
import { i18nManager } from "../../../i18n";
import { ActionType, FormStep, Schemes } from "../../../utils";
import { BoolHelper, Button, DOMHelper, FormControl, NoseurObject, TextInput } from "@rivtn/noseur";
import { ITransaction } from "../../../services";
import DynamicLoader from "../../helpers/DynamicLoader";

export interface PloutousCardProps {
    businessId: string;
    transaction: ITransaction;
    paymentSuccessful: () => void;
    stepState: [FormStep, React.Dispatch<React.SetStateAction<FormStep>>];
    loadingState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    setActions: (action: (type: ActionType) => void, genOtp?: () => void) => void;
    fetchTransaction: (onDone?: (result?: ITransaction, error?: any) => void) => void;
}

function PloutousCard(props: PloutousCardProps) {

    const transaction = props.transaction;
    const [_, setLoading] = props.loadingState;
    const labels = i18nManager.Labels.method.card;
    const cardOtpRef = React.useRef<HTMLDivElement>(null);
    const cardCvvRef = React.useRef<HTMLDivElement>(null);
    const cardPinRef = React.useRef<HTMLDivElement>(null);
    const cardNumberRef = React.useRef<HTMLDivElement>(null);
    const cardExpiryDateRef = React.useRef<HTMLDivElement>(null);
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [iswHostedFieldsInstance, setIswHostedFieldsInstance] = React.useState<any>();
    const [cardHolderName, setCardHolderName] = React.useState(`${transaction.customer.first_name} ${transaction.customer.last_name}`.trim());
    const [step, setStep] = props.stepState;
    const errorMap: NoseurObject<string> = {
        S56: "The card is not valid",
        EZ5: "Payment already processed",
        EZ81: "No bin was found for this pan",
        EZ1: "Error processing request null, please try again",
        SZ1: "An error occur while trying to charge this card",
    }

    React.useEffect(() => props.setActions(proceedToPay.bind(PloutousCard)), []);
    React.useEffect(() => {
        if (iswHostedFieldsInstance && step === FormStep.ENTER_CARD_DETAIL) {
            if (cardOtpRef.current) DOMHelper.removeAllChildren(cardOtpRef.current);
            if (cardPinRef.current) DOMHelper.removeAllChildren(cardPinRef.current);
            if (cardCvvRef.current) DOMHelper.removeAllChildren(cardCvvRef.current);
            if (cardNumberRef.current) DOMHelper.removeAllChildren(cardNumberRef.current);
            if (cardExpiryDateRef.current) DOMHelper.removeAllChildren(cardExpiryDateRef.current);
            loadIswScripts();
        }
    }, [step]);

    return (<>
        <DynamicLoader url="https://isw-hosted-fields.k8.isw.la/sdk.js" onLoad={loadIswScripts} />
        {step === FormStep.ENTER_CARD_DETAIL && (<span className="title">{labels.title}</span>)}
        <div className="card-info" style={step === FormStep.ENTER_CARD_DETAIL ? { display: "flex", marginTop: 30 } : { display: "none" }}>
            <span className="title">{labels.card_info}</span>
            <span className="desc">{labels.card_desc}</span>
            <div className="epi-input" id="p-card-number" ref={cardNumberRef} />
            <div className="dbl">
                <div className="epi-input" ref={cardExpiryDateRef} id="p-expiry-date" />
                <div className="epi-input" ref={cardCvvRef} id="p-cvv" />
            </div>
        </div>
        <div className="card-info" style={step === FormStep.ENTER_CARD_DETAIL ? { display: "flex" } : { display: "none" }}>
            <span className="title">{labels.card_holder_name}</span>
            <span className="desc">{labels.card_holder_name_desc}</span>
            <FormControl fill scheme={Schemes.RIVTN_PLOUTOUS}>
                <TextInput defaultValue={cardHolderName} name="name" placeholder={labels.card_name_placeholder}
                    onInputComplete={(v) => setCardHolderName(v)} onFirstInput={() => setErrorMessages([])} />
            </FormControl>
        </div>
        <div className="card-info" style={step === FormStep.ENTER_OTP ? { display: "flex" } : { display: "none" }}>
            <span className="title" style={{ textAlign: "center" }}>{labels.enter_otp}</span>
            <span className="desc" style={{ marginTop: 20, textAlign: "center" }}>{labels.enter_sent_otp}</span>
            <div className="epi-input" ref={cardOtpRef} id="p-otp" style={{ marginTop: 20, marginBottom: 20 }} />
        </div>
        <div className="card-info" style={step === FormStep.ENTER_PIN ? { display: "flex" } : { display: "none" }}>
            <span className="title" style={{ textAlign: "center" }}>{labels.enter_pin}</span>
            <span className="desc" style={{ marginTop: 20, textAlign: "center" }}>{labels.enter_card_pin}</span>
            <div className="epi-input" ref={cardPinRef} id="p-pin" style={{ marginTop: 20, marginBottom: 20 }} />
        </div>
        <div className="p-cardinal" style={step === FormStep.CARDINAL ? { display: "flex" } : { display: "none" }} />
        {errorMessages.map((errorMessage) => <span key={errorMessage} className="err">{errorMessage}</span>)}
        <Button raised fill scheme={Schemes.RIVTN_PLOUTOUS} text={
            step === FormStep.ENTER_CARD_DETAIL
                ? i18nManager.Labels.common.pay_now
                : ((step === FormStep.ENTER_OTP)
                    ? i18nManager.Labels.common.validate
                    : (step === FormStep.ENTER_PIN)
                        ? i18nManager.Labels.common.continue
                        : (step === FormStep.CARDINAL)
                            ? i18nManager.Labels.common.confirm_payment
                            : i18nManager.Labels.common.back)}
            className="pay" disabled={!(!(errorMessages.length && step !== FormStep.ENTER_OTP))} onClick={() => {
                setLoading(true);
                if (step === FormStep.ENTER_CARD_DETAIL) {
                    proceedToPay();
                } else if (step === FormStep.ENTER_PIN) {
                    iswHostedFieldsInstance?.makePayment(handlePayResponse);
                } else if (step === FormStep.ENTER_OTP) {
                    iswHostedFieldsInstance?.validatePayment(handleValidateResponse);
                } else if (step === FormStep.CARDINAL) {
                    setLoading(false);
                    setStep(FormStep.CONFIRM_PAYMENT);
                }
            }} />
        {(step === FormStep.ENTER_PIN || step === FormStep.ENTER_OTP || step === FormStep.CARDINAL) && (<Button raised outlined fill scheme={Schemes.RIVTN_PLOUTOUS} className="pay"
            text={i18nManager.Labels.common.back} style={{ marginTop: 20 }} onClick={() => {
                setErrorMessages([]);
                if (step === FormStep.ENTER_PIN || step === FormStep.CARDINAL) {
                    setStep(FormStep.ENTER_CARD_DETAIL);
                } else if (step === FormStep.ENTER_OTP) {
                    setStep(FormStep.ENTER_PIN);
                }
            }} />)}
    </>);

    function proceedToPay() {
        if (!cardHolderName.trim()) {
            setLoading(false);
            setErrorMessages([labels.card_holder_name_required]);
            return
        }
        iswHostedFieldsInstance?.getBinConfiguration((err: any, response: any) => {
            if (err != null && err.validationError === true) {
                setLoading(false);
                setErrorMessages([labels.card_input_invalid]);
                return;
            }

            if (err != null && err.networkError === true) {
                showNotification(err, null);
                return;
            }
            if (err !== null) {
                showNotification(err, null)
                return;
            }
            if (response.supportsPin) {
                setLoading(false);
                setStep(FormStep.ENTER_PIN);
                return;
            }
            iswHostedFieldsInstance?.makePayment(handlePayResponse);
        });
    }

    function handlePayResponse(err: any, response: any) {
        if (err != null && err.validationError === true) {
            showNotification(err, null);
            return;
        }

        if (err != null && err.networkError === true) {
            showNotification(err, null);
            return;
        }

        if (err != null) {
            showNotification(err, null)
            return;
        }

        if (response.requiresCentinelAuthorization === true) {
            setLoading(false);
            setStep(FormStep.CARDINAL);
            return;
        }

        setLoading(false);
        if (response?.responseCode === 'T0') {
            setStep(FormStep.ENTER_OTP);
            showNotification(null, response);
            return;
        } else {
            showNotification(null, err);
        }
    }

    function handleValidateResponse(err: any, response: any) {
        if (err != null && err.validationError === true) {
            showNotification(err, null);
            return;
        }

        if (err != null && err.networkError === true) {
            showNotification(err, null);
            return;
        }

        if (err != null) {
            showNotification(err, null);
            return;
        }

        if (response) {
            showNotification(null, response)
            return;
        } else {
            showNotification(null, err);
        }
    }

    function loadIswScripts() {
        const configuration = {
            fields: {
                cardNumber: {
                    selector: '#p-card-number',
                    placeholder: '0000  0000  0000  0000',
                    styles: {
                        height: "45px",
                        fontSize: '16px',
                        padding: '0px 0px 0px 10px',
                    }
                },
                expirationDate: {
                    selector: '#p-expiry-date',
                    placeholder: 'MM / YY',
                    styles: {
                        fontSize: '16px',
                        padding: '0px 0px 0px 10px',
                    }
                },
                cvv: {
                    selector: '#p-cvv',
                    placeholder: '***',
                    styles: {
                        fontSize: '16px',
                        padding: '0px 0px 0px 10px',
                    }
                },
                pin: {
                    selector: '#p-pin',
                    placeholder: '* * * *',
                    styles: {
                        fontSize: '16px',
                        padding: '0px 0px 0px 10px',
                        textAlign: 'center'
                    }
                },
                otp: {
                    selector: '#p-otp',
                    placeholder: '* * * * * *',
                    styles: {
                        fontSize: '16px',
                        padding: '0px 0px 0px 10px',
                        textAlign: 'center'
                    }
                }
            },
            cardinal: {
                activeClass: 'show',
                containerSelector: '.p-cardinal'
            },
            paymentParameters: {
                payableCode: transaction.pd.card.p,
                currencyCode: transaction.pd.card.c,
                merchantCode: transaction.pd.card.m,
                accountNumber: transaction.reference,
                dateOfPayment: transaction.expiry_date,
                amount: parseInt(transaction.amount as any) / 10,
                merchantCustomerName: transaction.reference.substring(0, 30),
                transactionReference: (transaction.transaction_id + "_" + transaction.service_code.substring(0, 3)) + "_CARD_" + Date.now(),
            }

        };
        (window as any).isw.hostedFields.create(configuration, (createError: any, hostedFieldsInstance: any) => {
            if (createError != null) {
                setErrorMessages([`${createError}`]);
                return;
            }
            hostedFieldsInstance.on('focus', function (event: any) {
                setErrorMessages([]);
                let fieldContainer = document.querySelector(event.selector);
                fieldContainer.style.borderBottomColor = '#a0c8e2';
            });
            hostedFieldsInstance.on('blur', function (event: any) {
                setErrorMessages([]);
                let fieldContainer = document.querySelector(event.selector);
                fieldContainer.style.borderBottomColor = '#e4e4e4';
            });
            hostedFieldsInstance.on('validation', function (event: any) {
            });
            hostedFieldsInstance.on('cardinal-response', ((err: any, response: any) => {
                setStep(FormStep.ENTER_CARD_DETAIL);
            }));
            setIswHostedFieldsInstance(hostedFieldsInstance);
        });
    }

    function showNotification(error: any, response: any) {
        setLoading(false);
        const errors: string[] = [];
        if (!error && !response) {
            error = {
                responseCode: "Z1",
            };
        }
        console.log("LOGGING THE ERROR CODE", error, "---", response);
        if (response) {
            let responseCode = response?.responseCode
            let responseMessage = response?.plainTextSupportMessage
            if (response.plainTextSupportMessage) {
                errors.push(`${responseMessage ?? (errorMap["S" + responseCode] ?? responseCode)}`);
            } else if (responseCode !== "00") {
                errors.push(`${errorMap["S" + responseCode] ?? responseCode}`);
            }
        } else if (error) {
            let errorCode = error?.responseCode;
            let networkError = error?.networkError;
            let validationError = error?.validationError;
            if (validationError) {
                errors.push(`There's Validation Error`);
            }
            if (networkError) {
                errors.push(`Network Error`);
            }
            if (errorCode) {
                errors.push(`${errorMap["E" + errorCode] ?? errorCode}`);
            }
        }
        setErrorMessages(errors);
        if (response?.responseCode === '00') {
            console.log("" + response?.responseCode + ": Transaction Successful");
            setStep(FormStep.CONFIRM_PAYMENT);
            return;
        }
    }

}

export default PloutousCard;
